// Live spectrogram + playback for the call review (13 Oct 2026, user: "you can only zoom in, and cannot zoom out" +
// "is it possible to add sound playback?").
// Engine 0.4.12 saves a longer sound clip around each call (entry.ctx: 16-bit WAV, t0..t1 seconds in the file). The
// browser parses it itself (no resampling — a 192 kHz clip keeps everything up to 96 kHz), draws the spectrogram of
// whatever time / frequency window is in view, and plays any part of it (speed, loop, band filter).
// Calls from older engine versions have only the short frame sound (entry.audio): the same code works on that.

// ---------------------------------------------------------------- WAV
/** ArrayBuffer of a PCM WAV (16 / 24 / 32-bit int, 32-bit float; channel 1 is used) -> { fs, x: Float32Array } */
export function parseWav(buf) {
  const dv = new DataView(buf);
  const tag = (o) => String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') throw new Error('not a WAV file');
  let o = 12; let fmt = null; let data = null;
  while (o + 8 <= dv.byteLength) {
    const id = tag(o); const size = dv.getUint32(o + 4, true);
    if (id === 'fmt ') fmt = { format: dv.getUint16(o + 8, true), ch: dv.getUint16(o + 10, true), fs: dv.getUint32(o + 12, true), bits: dv.getUint16(o + 22, true) };
    if (id === 'data') { data = { off: o + 8, size: Math.min(size, dv.byteLength - o - 8) }; break; }
    o += 8 + size + (size % 2);
  }
  if (!fmt || !data) throw new Error('WAV without fmt / data');
  const bps = fmt.bits / 8; const n = Math.floor(data.size / (bps * fmt.ch)); const x = new Float32Array(n);
  const float = fmt.format === 3;
  for (let i = 0; i < n; i += 1) {
    const p = data.off + i * bps * fmt.ch;
    if (float) x[i] = dv.getFloat32(p, true);
    else if (fmt.bits === 16) x[i] = dv.getInt16(p, true) / 32768;
    else if (fmt.bits === 24) { const v = dv.getUint8(p) | (dv.getUint8(p + 1) << 8) | (dv.getInt8(p + 2) << 16); x[i] = v / 8388608; }
    else if (fmt.bits === 32) x[i] = dv.getInt32(p, true) / 2147483648;
    else x[i] = (dv.getUint8(p) - 128) / 128;
  }
  return { fs: fmt.fs, x };
}

// ---------------------------------------------------------------- FFT (radix 2, in place)
function fft(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i += 1) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { let t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len; const wr = Math.cos(ang); const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1; let ci = 0;
      for (let k = 0; k < len / 2; k += 1) {
        const a = i + k; const b = a + len / 2;
        const tr = re[b] * cr - im[b] * ci; const ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
        const nr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = nr;
      }
    }
  }
}

// magma colour map (16 stops, interpolated) — the same look as the engine's pictures
const MAGMA = [[0, 0, 4], [12, 8, 38], [35, 15, 76], [63, 15, 114], [90, 22, 126], [116, 33, 129], [142, 43, 128], [169, 51, 123],
  [196, 60, 113], [221, 74, 99], [240, 96, 85], [250, 125, 80], [254, 157, 95], [254, 189, 119], [253, 220, 149], [252, 253, 191]];
const LUT = (() => {
  const out = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i += 1) {
    const p = (i / 255) * (MAGMA.length - 1); const k = Math.min(MAGMA.length - 2, Math.floor(p)); const f = p - k;
    for (let c = 0; c < 3; c += 1) out[i * 3 + c] = MAGMA[k][c] + (MAGMA[k + 1][c] - MAGMA[k][c]) * f;
  }
  return out;
})();

/** window length (power of 2) with balanced time / frequency detail for a view: L = fs * sqrt(seconds / Hz) */
export function balancedWindow(fs, view) {
  const dur = Math.max(1e-4, view.t1 - view.t0); const band = Math.max(1, view.f1 - view.f0);
  let n = 64; const want = fs * Math.sqrt(dur / band);
  while (n < want && n < 32768) n <<= 1;
  return n;
}
export const DETAIL_STEPS = [-3, -2, -1, 0, 1, 2, 3];   // window / 8 .. x 8 of the automatic window
/** the window used for a view: the automatic one never gets shorter than the zoomed-out (full) view's window, so
 * zooming in only enlarges the contour seen zoomed out (user 13 Oct 2026, all calls); `detail` < 0 = sharper in time
 * (shorter window), > 0 = sharper in frequency (longer window). */
export function windowFor(fs, view, { minView = null, detail = 0 } = {}) {
  let n = balancedWindow(fs, view);
  [].concat(minView || []).forEach((m) => { n = Math.max(n, balancedWindow(fs, m)); });   // one view or a list
  const d = Math.max(-3, Math.min(3, Math.round(detail || 0)));
  n = d >= 0 ? n << d : n >> -d;
  return Math.max(32, Math.min(65536, n));
}

/**
 * Draws the spectrogram of sig (Float32 x, fs, t0 = file time of sample 0) for view {t0, t1, f0, f1} into canvas.
 * Window: windowFor() (see above). Levels are normalised per frequency row (median of the row = 0 dB, as the
 * engine's pictures), 0..range dB shown. Returns {nfft, ms, hz} (window length, its duration and bin spacing).
 */
export function drawSpectrogram(canvas, sig, view, { range: range0 = 30, maxWidth = 1600, minView = null, detail = 0 } = {}) {
  let range = range0;
  if (!canvas || !sig) return null;
  const W = Math.min(maxWidth, Math.max(200, Math.round(canvas.clientWidth * (window.devicePixelRatio || 1))));
  const H = Math.max(120, Math.round(W / 2));
  if (canvas.width !== W) canvas.width = W;
  if (canvas.height !== H) canvas.height = H;
  const { fs, x } = sig;
  const dur = view.t1 - view.t0; const band = Math.max(1, view.f1 - view.f0);
  const nfft = windowFor(fs, view, { minView, detail });
  const win = new Float32Array(nfft); for (let i = 0; i < nfft; i += 1) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (nfft - 1));
  const re = new Float32Array(nfft); const im = new Float32Array(nfft);
  const binHz = fs / nfft; const nb = nfft / 2;
  // frames: at most one per pixel column (hop >= 1 sample), and a work budget so a long window stays fast
  const cols = W;
  const need = Math.ceil(dur * fs) + 1;   // a long window on a short view: frames may overlap by more than 75 %
  const budget = Math.floor(6e7 / (nfft * Math.log2(nfft)));
  const F = Math.max(2, Math.min(cols, need, budget));
  const rowBin = new Float32Array(H);
  for (let r = 0; r < H; r += 1) rowBin[r] = (view.f1 - ((r + 0.5) / H) * band) / binHz;
  const levF = new Float32Array(F * H); const spec = new Float32Array(nb);
  for (let j = 0; j < F; j += 1) {
    const tc = view.t0 + ((j + 0.5) / F) * dur;
    const start = Math.round((tc - sig.t0) * fs - nfft / 2);
    for (let i = 0; i < nfft; i += 1) { const k = start + i; re[i] = (k >= 0 && k < x.length ? x[k] : 0) * win[i]; im[i] = 0; }
    fft(re, im);
    for (let k = 0; k < nb; k += 1) spec[k] = 10 * Math.log10(re[k] * re[k] + im[k] * im[k] + 1e-20);
    for (let r = 0; r < H; r += 1) {
      const bn = rowBin[r];
      if (bn < 0 || bn >= nb - 1) { levF[r * F + j] = -200; } else {   // between bins: linear (a frequency zoom stays smooth)
        const b0 = Math.floor(bn); const w = bn - b0; levF[r * F + j] = spec[b0] * (1 - w) + spec[b0 + 1] * w;
      }
    }
  }
  const lev = new Float32Array(cols * H);
  for (let r = 0; r < H; r += 1) for (let c = 0; c < cols; c += 1) lev[r * cols + c] = levF[r * F + Math.min(F - 1, Math.floor((c * F) / cols))];
  // per-row median (noise floor) -> 0 dB
  const tmp = new Float32Array(cols);
  for (let r = 0; r < H; r += 1) {
    const row = lev.subarray(r * cols, (r + 1) * cols); tmp.set(row); tmp.sort();
    const med = tmp[cols >> 1];
    for (let c = 0; c < cols; c += 1) row[c] = row[c] <= -199 ? -999 : row[c] - med;
  }
  // colour scale as the engine's pictures: 0 dB .. max(18 dB, 99.7th percentile of what is shown)
  const smp = []; const stepS = Math.max(1, Math.floor((cols * H) / 20000));
  for (let i = 0; i < cols * H; i += stepS) if (lev[i] > -998) smp.push(lev[i]);
  smp.sort((p, q) => p - q);
  range = Math.max(18, smp.length ? smp[Math.floor(smp.length * 0.997)] : range);
  const ctx = canvas.getContext('2d'); const img = ctx.createImageData(W, H); const d = img.data;
  for (let i = 0; i < cols * H; i += 1) {
    const v = lev[i];
    const q = v <= -998 ? 0 : Math.max(0, Math.min(255, Math.round(((v - 2) / (range - 2)) * 255)));   // 2 dB: noise floor stays dark
    d[i * 4] = LUT[q * 3]; d[i * 4 + 1] = LUT[q * 3 + 1]; d[i * 4 + 2] = LUT[q * 3 + 2]; d[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return { nfft, ms: (1000 * nfft) / fs, hz: fs / nfft };
}

// ---------------------------------------------------------------- loading
const cache = new Map();
/** -> Promise<{fs, x, t0, t1, full}>  (ctx clip when the bundle has one, else the frame sound) */
export function loadCallSound(entry) {
  const src = entry.ctx && entry.ctx.url ? { url: entry.ctx.url, t0: entry.ctx.t0, full: true }
    : (entry.audio && entry.audio.url ? { url: entry.audio.url, t0: entry.audio.t0 ?? entry.x0, full: false } : null);
  if (!src) return Promise.reject(new Error('no sound for this call'));
  if (!cache.has(src.url)) {
    cache.set(src.url, fetch(src.url).then((r) => { if (!r.ok) throw new Error(`sound ${r.status}`); return r.arrayBuffer(); })
      .then((b) => { const s = parseWav(b); return { ...s, t0: Number(src.t0) || 0, t1: (Number(src.t0) || 0) + s.x.length / s.fs, full: src.full }; }));
    if (cache.size > 40) cache.delete(cache.keys().next().value);
  }
  return cache.get(src.url);
}

// ---------------------------------------------------------------- playback
let audioCtx = null;
const ac = () => { if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)(); return audioCtx; };

/**
 * Plays sig between a and b (file seconds) at speed; band [lo, hi] Hz filters the sound (null = no filter).
 * Returns { stop(), position() -> file seconds or null when finished }.
 */
export function play(sig, a, b, { speed = 1, loop = false, band = null } = {}) {
  const ctx = ac();
  if (ctx.state === 'suspended') ctx.resume();
  const i0 = Math.max(0, Math.floor((a - sig.t0) * sig.fs)); const i1 = Math.min(sig.x.length, Math.ceil((b - sig.t0) * sig.fs));
  if (i1 - i0 < 16) return { stop() {}, position: () => null };
  // keep the original rate when the browser allows it (up to 384 kHz in current browsers); else decimate
  // browsers refuse rates under 3 kHz (blue / fin whale sounds of the library): those are upsampled (linear)
  let rate = sig.fs; let step = 1;
  while (rate > 384000) { rate /= 2; step *= 2; }
  const up = rate < 8000 ? Math.ceil(8000 / rate) : 1; rate *= up; step /= up;
  const n = Math.floor((i1 - i0) / step);
  let buf;
  try { buf = ctx.createBuffer(1, n, rate); } catch (e) { step *= Math.ceil(rate / 96000); rate = sig.fs / step; buf = ctx.createBuffer(1, Math.floor((i1 - i0) / step), rate); }
  const ch = buf.getChannelData(0);
  const at = (q) => { const k = i0 + q; const k0 = Math.floor(k); const w = k - k0; return k0 + 1 < sig.x.length ? sig.x[k0] * (1 - w) + sig.x[k0 + 1] * w : sig.x[Math.min(k0, sig.x.length - 1)]; };
  let peak = 1e-6; for (let i = 0; i < ch.length; i += 1) { const v = at(i * step); ch[i] = v; if (Math.abs(v) > peak) peak = Math.abs(v); }
  const g = Math.min(20, 0.9 / peak); for (let i = 0; i < ch.length; i += 1) ch[i] *= g;      // normalise: faint calls audible
  const src = ctx.createBufferSource(); src.buffer = buf; src.playbackRate.value = speed; src.loop = loop;
  let node = src;
  if (band) {          // filters work on the played (speed-shifted) sound: cut-offs move with the speed
    const ny = ctx.sampleRate / 2 * 0.98;
    if (band[0] * speed > 20) { const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = Math.min(ny, band[0] * speed); node.connect(hp); node = hp; }
    if (band[1] * speed < ny) { const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = band[1] * speed; node.connect(lp); node = lp; }
  }
  node.connect(ctx.destination);
  const startAt = ctx.currentTime; const len = (i1 - i0) / sig.fs;
  src.start();
  let done = false; src.onended = () => { done = true; };
  return {
    stop() { try { src.stop(); } catch (e) { /* already stopped */ } done = true; },
    position() {
      if (done) return null;
      const el = (ctx.currentTime - startAt) * speed;
      return a + (loop ? el % len : Math.min(el, len));
    },
  };
}
